import { Avatar, Box, Flex, Text } from '@chakra-ui/react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineCamera } from 'react-icons/hi2';

/**
 * Avatar editor for the Profile drawer's Personal tab: the user's photo (or
 * initials fallback) with a camera badge that opens a file picker, plus a
 * "Remove photo" affordance when a photo is set. Presentational — the
 * upload/removal mutations and toasts stay in each app's adapter (AIM:
 * `useProfileData`; SMS: the `useUploadProfilePhoto` / `useRemoveProfilePhoto`
 * query hooks). Moved here from AIM's `profileComponent` so SMS gets the same
 * surface.
 */
export interface ProfilePhotoSectionProps {
  profilePhoto?: string | null;
  userName: string;
  onPhotoChange: (file: File) => void;
  onPhotoRemove: () => void;
  isUploading?: boolean;
}

export function ProfilePhotoSection({
  profilePhoto,
  userName,
  onPhotoChange,
  onPhotoRemove,
  isUploading = false,
}: ProfilePhotoSectionProps) {
  const { t } = useTranslation();
  const photoInputRef = useRef<HTMLInputElement>(null);

  return (
    <Flex direction="column" alignItems="center" gap={2}>
      <Box
        position="relative"
        cursor={isUploading ? 'wait' : 'pointer'}
        onClick={() => !isUploading && photoInputRef.current?.click()}
        aria-label={t('profile.changePhoto', 'Change profile photo')}
        role="button"
      >
        <Avatar.Root size="2xl" data-e2e="profileAvatar">
          {profilePhoto && <Avatar.Image src={profilePhoto} />}
          <Avatar.Fallback name={userName} />
        </Avatar.Root>
        <Flex
          position="absolute"
          bottom={0}
          right={0}
          bg="surface.primary"
          border="2px solid"
          borderColor="border.secondary"
          borderRadius="full"
          p={1}
          alignItems="center"
          justifyContent="center"
        >
          <HiOutlineCamera size={14} />
        </Flex>
      </Box>
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onPhotoChange(file);
          if (photoInputRef.current) photoInputRef.current.value = '';
        }}
        data-e2e="profilePhotoInput"
      />
      {profilePhoto && (
        <Text
          fontSize="xs"
          color="text.secondary"
          cursor="pointer"
          _hover={{ color: 'text.primary' }}
          onClick={onPhotoRemove}
          aria-label={t('profile.removePhoto', 'Remove profile photo')}
          data-e2e="removeProfilePhoto"
        >
          {t('profile.removePhoto', 'Remove photo')}
        </Text>
      )}
    </Flex>
  );
}

export default ProfilePhotoSection;
